import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePortal } from "../../../context/PortalContext";
import { PORTALS } from "../../../utils/portalTypes";
import AuthRegisterByLinkFlow from "../Auth/AuthRegisterByLinkFlow";
import RegistrationByLinkSuccess from "../Auth/RegistrationByLinkSuccess";
import {
  RegistrationLinkError,
  RegistrationLinkLoading,
} from "../Auth/RegistrationLinkState";
import authAPI from "../../../services/authApi";
import a from "../Auth/auth.module.css";
import Seo from "../../Seo";

const portal = PORTALS.patient;

export default function PatientRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("link");
  const subdomainFromQuery = String(searchParams.get("subdomain") || "")
    .trim()
    .toLowerCase();
  const { subdomain: portalSubdomain, clinic } = usePortal();
  const subdomain = portalSubdomain || subdomainFromQuery || null;
  const [linkInfo, setLinkInfo] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [linkLoading, setLinkLoading] = useState(Boolean(linkToken));
  const [byLinkPending, setByLinkPending] = useState(false);
  const [byLinkSubmittedEmail, setByLinkSubmittedEmail] = useState("");
  const [byLinkResult, setByLinkResult] = useState(null);
  const [byLinkSubmitting, setByLinkSubmitting] = useState(false);
  const [byLinkErr, setByLinkErr] = useState(null);

  useEffect(() => {
    if (!linkToken) {
      navigate(portal.login, { replace: true });
    }
  }, [linkToken, navigate]);

  useEffect(() => {
    if (!linkToken) return;
    setLinkLoading(true);
    authAPI
      .getRegistrationLinkInfo(linkToken)
      .then((r) => {
        setLinkInfo(r.link);
        setLinkError(null);
      })
      .catch((e) => {
        setLinkError(
          e.response?.data?.error || "Ссылка недействительна или истекла",
        );
      })
      .finally(() => setLinkLoading(false));
  }, [linkToken]);

  const submitByLink = async (data) => {
    setByLinkErr(null);
    setByLinkSubmitting(true);
    try {
      const res = await authAPI.registerByLink({
        token: linkToken,
        subdomain,
        registrationType: data.registrationType || "family",
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        children: data.children,
        dateOfBirth: data.dateOfBirth,
        consent_pd: Boolean(data.consent_pd),
      });
      setByLinkSubmittedEmail(String(data.email || "").trim());
      setByLinkResult({
        registrationPending: Boolean(res?.registration_pending),
        reusedParent: Boolean(res?.reused_parent),
        reusedSolo: Boolean(res?.reused_solo),
        message: res?.message || "",
      });
      setByLinkPending(true);
    } catch (e) {
      setByLinkErr(
        e.response?.data?.error || e.message || "Не удалось отправить заявку",
      );
    } finally {
      setByLinkSubmitting(false);
    }
  };

  if (!linkToken) {
    return null;
  }

  if (byLinkPending) {
    return (
      <>
        <Seo noindex title="Заявка отправлена" />
        <RegistrationByLinkSuccess
          clubName={clinic?.name}
          email={byLinkSubmittedEmail}
          loginPath={portal.login}
          registrationPending={byLinkResult?.registrationPending ?? true}
          reusedParent={byLinkResult?.reusedParent ?? false}
          reusedSolo={byLinkResult?.reusedSolo ?? false}
          message={byLinkResult?.message || ""}
        />
      </>
    );
  }

  if (linkLoading) {
    return (
      <>
        <Seo noindex title="Заявка в клуб" />
        <RegistrationLinkLoading loginPath={portal.login} />
      </>
    );
  }

  if (linkError) {
    return (
      <>
        <Seo noindex title="Ссылка недоступна" />
        <RegistrationLinkError message={linkError} loginPath={portal.login} />
      </>
    );
  }

  return (
    <>
      <Seo noindex title="Заявка в клуб" />
      <AuthRegisterByLinkFlow
        clubName={clinic?.name}
        linkInfo={linkInfo}
        portalId="patient"
        backTo={portal.login}
        backLabel="Назад"
        loading={byLinkSubmitting}
        error={byLinkErr}
        onSubmit={submitByLink}
        footer={
          <div className={a.footerLinksModern}>
            <Link to={portal.login}>Уже есть аккаунт? Войти</Link>
          </div>
        }
      />
    </>
  );
}
